import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  addAccount,
  addNewAccountAccountId,
  CardAttributeUpdateAction,
  deleteAccount,
  updateAccount,
} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {CardModel} from '../../../models/api/cardModel';
import {AccountAvatar} from '../../common/avatars/accountAvatar';
import {InlineEditingAccountPopover} from '../../common/inlineEditing/inlineEditingAccountPopover';
import {ContactInformationAttribute} from './contactInformationAttribute';

/*
 * Props.
 */

interface AccountProps {
  card: CardModel;
  account?: AccountModel;
  updateCardAttributeAsync: (action: CardAttributeUpdateAction) => Promise<void>;
  updateUnsavedCard: (action: CardAttributeUpdateAction) => void;
}

/*
 * Style.
 */

const StyledAccountDiv = styled.div`
  display: flex;
  flex-flow: row;
  margin: 8px 0 7px 0;
`;

const StyledAccountAvatar = styled(AccountAvatar)`
  margin-right: 8px;
`;

const StyledAccountIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledEmptyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const Account: FC<AccountProps> = (props) => {
  const {
    card: {accountId},
    account,
    updateCardAttributeAsync,
    updateUnsavedCard,
  } = props;

  if (accountId === undefined) {
    return null;
  }
  const isAddingAccount = accountId === addNewAccountAccountId;

  return (
    <ContactInformationAttribute<number>
      interactionId="contact-details-account"
      title={<Trans id="contact-details-contact-information-account">Account</Trans>}
      renderValue={({isEditing, editedValue, onEditSave, onEditCancel}) => {
        if (isEditing) {
          return (
            <InlineEditingAccountPopover
              renderButtonContent={(renderAccountId) => {
                if (renderAccountId === addNewAccountAccountId) {
                  return (
                    <StyledEmptyDiv>
                      <Trans id="contact-details-contact-information-select-account">Select account</Trans>
                    </StyledEmptyDiv>
                  );
                }
                return renderAccount(account);
              }}
              accountId={editedValue}
              onEditSave={onEditSave}
              onEditCancel={onEditCancel}
            />
          );
        }

        return <StyledAccountDiv>{renderAccount(account)}</StyledAccountDiv>;
      }}
      copyValue={`${account?.name} (#${accountId})`}
      currentAttributeValue={accountId}
      onSave={async (newAccountId: number) => {
        if (isAddingAccount) {
          return updateCardAttributeAsync(addAccount(newAccountId));
        }
        return updateCardAttributeAsync(updateAccount(accountId, newAccountId));
      }}
      onDelete={() => {
        const action = deleteAccount(accountId);
        // No need to hit the backend if the user deletes the account before it's saved
        if (isAddingAccount) {
          return updateUnsavedCard(action);
        }
        return updateCardAttributeAsync(action);
      }}
      shouldHideCopyOption
      shouldStartInEditMode={isAddingAccount}
    />
  );
};

/*
 * Helpers.
 */

const renderAccount = (account?: AccountModel) => {
  if (!account) {
    return '-';
  }
  return (
    <>
      {account.avatarUrl ? (
        <StyledAccountAvatar account={account} avatarIconSize={8} size={VisualSizesEnum.SMALL} />
      ) : (
        <UserThemeConsumer>
          {(theme) => <StyledAccountIcon name="briefcaseOutlineSmall" color={theme.greys.shade70} />}
        </UserThemeConsumer>
      )}
      {account.name}
    </>
  );
};
